import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { cases } from '../data/cases';
import { Reveal } from '../components/Reveal';
import { CTABand } from '../components/CTABand';

export default function Portfolio() {
  const { lang, t } = useLanguage();
  const p = t.portfolio;

  return (
    <div data-testid="page-portfolio">
      {/* Header */}
      <section className="px-6 lg:px-12 pt-32 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-2">
            <Reveal>
              <p className="overline">{p.overline}</p>
            </Reveal>
          </div>

          <div className="md:col-span-10">
            <Reveal delay={0.05}>
              <h1
                className="font-serif font-light text-[2.6rem] md:text-[5.4rem] leading-[1] tracking-tight"
                style={{ color: 'var(--ink)' }}
              >
                {p.title}
              </h1>
            </Reveal>

            <Reveal delay={0.12}>
              <p
                className="mt-8 text-[17px] leading-relaxed max-w-2xl"
                style={{ color: 'var(--ink-2)' }}
              >
                {p.lead}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Cases list */}
      <section className="px-6 lg:px-12 pb-24">
        <div
          className="max-w-[1400px] mx-auto"
          style={{ borderTop: '1px solid var(--rule)' }}
        >
          {cases.map((c, i) => {
            const isExternal = Boolean(c.url && c.url !== '#');
            const Wrapper = isExternal ? 'a' : 'div';

            const wrapperProps = isExternal
              ? {
                  href: c.url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
              : {};

            return (
              <Reveal key={`${c.id}-${c.year}`} delay={(i % 3) * 0.05}>
                <Wrapper
                  {...wrapperProps}
                  className="group grid md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 transition-colors"
                  style={{ borderBottom: '1px solid var(--rule)' }}
                  data-testid={`case-${c.id}`}
                >
                  <div className="md:col-span-1">
                    <span
                      className="text-[12px] tracking-[0.16em]"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div className="md:col-span-3">
                    <p className="overline">{c.service[lang]}</p>

                    <p
                      className="mt-3 text-[13px]"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {c.year}
                    </p>
                  </div>

                  <div className="md:col-span-7">
                    <h3
                      className="font-serif text-3xl md:text-[2.6rem] leading-[1.08] font-light"
                      style={{ color: 'var(--ink)' }}
                    >
                      {c.name}
                    </h3>

                    <p
                      className="mt-5 text-[15px] md:text-[16px] leading-relaxed max-w-2xl"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      {c.description[lang]}
                    </p>

                    <span
                      className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-wide italic"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {isExternal ? p.visit : p.internal}

                      {isExternal && (
                        <ArrowUpRight
                          size={14}
                          className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                        />
                      )}
                    </span>
                  </div>

                  <div className="hidden md:flex md:col-span-1 justify-end">
                    {isExternal && (
                      <ArrowUpRight
                        size={24}
                        className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                        style={{ color: 'var(--ink)' }}
                      />
                    )}
                  </div>
                </Wrapper>
              </Reveal>
            );
          })}
        </div>
      </section>

      <CTABand
        title={p.closingTitle}
        body={p.closingBody}
        testid="portfolio-cta"
      />
    </div>
  );
}
